import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

const CHANNEL_NAME = 'kconnect-tab-manager';
const TAB_ID_KEY = 'kc_active_tab_id';

/**
 * Hook to manage single-tab-per-device policy using BroadcastChannel API
 * When a new tab is opened, existing tabs are signed out
 */
export function useTabManager({ isAuthenticated }) {
  const channelRef = useRef(null);
  const tabIdRef = useRef(null);
  const isAnnouncedRef = useRef(false);

  // Generate unique tab ID
  const generateTabId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Handle being kicked out by another tab
  const handleForceSignOut = useCallback(async (reason = 'new_tab') => {
    logger.debug('Tab forced to sign out:', reason);

    // Sign out locally only (don't invalidate session for other devices)
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      logger.debug('Local signOut error (ignored):', e.message);
    }

    // Clear local storage auth token
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
      localStorage.removeItem(storageKey);
    } catch (e) {
      // Ignore
    }

    // Show toast and redirect
    toast.info('Zostałeś wylogowany - otwarto aplikację w innej karcie.', {
      duration: 5000,
    });

    // Small delay to ensure toast is visible, then redirect
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }, []);

  // Handle messages from other tabs
  const handleMessage = useCallback((event) => {
    const { type, tabId, timestamp } = event.data;

    // Ignore messages from self
    if (tabId === tabIdRef.current) return;

    switch (type) {
      case 'NEW_TAB_OPENED':
        // Another tab is claiming master status - sign out this tab
        logger.debug('Received NEW_TAB_OPENED from another tab');
        if (isAuthenticated) {
          handleForceSignOut('new_tab');
        }
        break;

      case 'PING':
        // Respond to ping if we're authenticated
        if (isAuthenticated && channelRef.current) {
          channelRef.current.postMessage({
            type: 'PONG',
            tabId: tabIdRef.current,
            timestamp: Date.now(),
          });
        }
        break;

      default:
        break;
    }
  }, [isAuthenticated, handleForceSignOut]);

  // Announce this tab and kick out others
  const announceTab = useCallback(() => {
    if (!channelRef.current || isAnnouncedRef.current) return;

    logger.debug('Announcing new tab, kicking out existing tabs');

    // Store our tab ID in sessionStorage (tab-specific)
    sessionStorage.setItem(TAB_ID_KEY, tabIdRef.current);

    // Broadcast to other tabs
    channelRef.current.postMessage({
      type: 'NEW_TAB_OPENED',
      tabId: tabIdRef.current,
      timestamp: Date.now(),
    });

    isAnnouncedRef.current = true;
  }, []);

  // Initialize BroadcastChannel
  useEffect(() => {
    // Only manage tabs when authenticated
    if (!isAuthenticated) {
      // Clean up if we were authenticated before
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      isAnnouncedRef.current = false;
      return;
    }

    // Generate tab ID if not already set
    if (!tabIdRef.current) {
      tabIdRef.current = generateTabId();
    }

    // Check for BroadcastChannel support
    if (typeof BroadcastChannel === 'undefined') {
      logger.debug('BroadcastChannel not supported, using localStorage fallback');

      // Fallback to localStorage storage event
      const handleStorageEvent = (event) => {
        if (event.key === TAB_ID_KEY && event.newValue && event.newValue !== tabIdRef.current) {
          logger.debug('Detected new tab via localStorage');
          handleForceSignOut('new_tab');
        }
      };

      window.addEventListener('storage', handleStorageEvent);

      // Announce via localStorage
      localStorage.setItem(TAB_ID_KEY, tabIdRef.current);

      return () => {
        window.removeEventListener('storage', handleStorageEvent);
      };
    }

    // Create BroadcastChannel
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = handleMessage;

      // Small delay before announcing to avoid race conditions
      const announceTimeout = setTimeout(() => {
        announceTab();
      }, 100);

      return () => {
        clearTimeout(announceTimeout);
        if (channelRef.current) {
          channelRef.current.close();
          channelRef.current = null;
        }
      };
    } catch (e) {
      logger.error('Failed to create BroadcastChannel:', e);
    }
  }, [isAuthenticated, generateTabId, handleMessage, announceTab, handleForceSignOut]);

  return {
    tabId: tabIdRef.current,
  };
}
